/**
 * A signed-in player's days, from the server into this device (ACC-004,
 * docs/phase-5-plan.md §4.3).
 *
 * On load, a signed-in page asks for the account's days and folds them into the local
 * history, so statistics, the calendar and the streak are the same on every device, and
 * so today's table continues the ACCOUNT's run rather than this device's own (ACC-003's
 * recorded dependency: without this, a second device could send its shots on top of
 * another device's run).
 *
 * The server's record wins, because it is the one that counts. One exception: a day
 * this device has played FURTHER than the server has recorded, shot for shot the same up
 * to where the server stops, is this device's own shots still waiting to be sent
 * (offline, or a reload mid-send). Those are kept, and the send queue sends the rest.
 *
 * Every day from the server goes through the same validator as local storage (history
 * readRecord): the reply is untrusted input like any other.
 */
import type { ShotInput } from '@potted/sim';
import type { KeyValueStore } from '../settings/settings.ts';
import { readRecord, type DayRecord, type History } from './history.ts';
import { CHECKED_KEY, type Checked } from './send.ts';

export const ACCOUNT_HISTORY_URL = '/api/me/history';

/** Waits at most this long: the game never waits on the account to start. */
const FETCH_TIMEOUT_MS = 3000;

/** The account's days from the reply; undefined when not signed in, or anything odd. */
export function readAccountDays(status: number, body: unknown): DayRecord[] | undefined {
  if (status !== 200 || typeof body !== 'object' || body === null) return undefined;
  const b = body as Record<string, unknown>;
  if (b['signedIn'] !== true || !Array.isArray(b['days'])) return undefined;
  const days: DayRecord[] = [];
  for (const raw of b['days'] as unknown[]) {
    const day = readRecord(raw);
    // A day that does not validate is left out; the rest still count.
    if (day !== undefined) days.push(day);
  }
  return days;
}

const sameShot = (a: ShotInput, b: ShotInput): boolean =>
  a.dx === b.dx &&
  a.dy === b.dy &&
  a.power === b.power &&
  a.spinX === b.spinX &&
  a.spinY === b.spinY;

/** Does `longer` start with every shot of `shorter`, in order? */
function extends_(longer: readonly ShotInput[], shorter: readonly ShotInput[]): boolean {
  return (
    longer.length > shorter.length &&
    shorter.every((shot, i) => {
      const mine = longer[i];
      return mine !== undefined && sameShot(mine, shot);
    })
  );
}

/** The day to keep: the server's, unless this device's is its unsent continuation. */
export function mergeDay(local: DayRecord | undefined, server: DayRecord): DayRecord {
  if (local === undefined) return server;
  const unsentContinuation =
    server.strokes === undefined &&
    local.physicsVersion === server.physicsVersion &&
    extends_(local.shots, server.shots);
  return unsentContinuation ? local : server;
}

/**
 * Fold the account's TODAY into this device, so this device carries on today's run, and
 * set the send queue to what the server holds, so it sends exactly what it lacks.
 *
 * Only today (ACC-011): past days are no longer copied into the device's history. They
 * are shown from the account while signed in (account-view.ts), so they never linger
 * for the next person to sign in on this device.
 */
export function syncAccountDays(
  history: History,
  storage: KeyValueStore | undefined,
  days: readonly DayRecord[],
  todayKey: string | undefined,
): void {
  for (const server of days) {
    if (server.dateKey !== todayKey) continue;
    const local = history.get(server.dateKey);
    const kept = mergeDay(local, server);
    if (kept !== local) history.put(kept);
    const checked: Checked = {
      dateKey: server.dateKey,
      sent: server.shots.length,
      ...(server.strokes === undefined
        ? {}
        : { stopped: true, result: { strokes: server.strokes, cleared: true } }),
    };
    try {
      storage?.setItem(CHECKED_KEY, JSON.stringify(checked));
    } catch {
      // Blocked storage: the queue starts from nothing and the server puts it right.
    }
  }
}

/** Ask for the account's days. Undefined when not signed in, offline, or slow. */
export async function fetchAccountDays(): Promise<DayRecord[] | undefined> {
  try {
    const response = await fetch(ACCOUNT_HISTORY_URL, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    return readAccountDays(response.status, await response.json());
  } catch {
    return undefined;
  }
}
